import { Check, Loader2, Lock, ShieldCheck, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useConnectionRequest } from "../../../features/app-connections/model/use-connection-request";
import { useT } from "../../../shared/i18n/i18n";

// The one screen where a person decides whether an assistant gets to see their
// work. It says who is asking, what they would be able to do in the words the
// product uses everywhere else, and that it never exceeds the person's own
// access — because that is the only thing anyone needs to know to answer.
export function ConnectPage({ requestId }: { requestId: string }) {
  const t = useT();
  const connection = useConnectionRequest(requestId);
  const [error, setError] = useState<string | null>(null);

  async function answer(accept: boolean) {
    setError(null);
    try {
      await connection.answer(accept);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("connect.failed"));
    }
  }

  if (connection.isPending) {
    return (
      <main className="grid min-h-screen place-items-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (connection.isUnavailable || !connection.request) {
    return (
      <main className="grid min-h-screen place-items-center px-4">
        <div className="w-full max-w-sm text-center">
          <p className="text-sm font-medium">{t("connect.expired")}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t("connect.expiredHint")}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="grid min-h-screen place-items-center px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-border/70 bg-card p-6 shadow-sm">
        <div className="flex size-10 items-center justify-center rounded-lg bg-muted">
          <Lock className="size-5 text-muted-foreground" />
        </div>
        <h1 className="mt-4 text-lg font-semibold">{t("connect.title", { app: connection.request.clientName })}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {connection.userEmail ? t("connect.asYou", { email: connection.userEmail }) : t("connect.asYouUnknown")}
        </p>

        <ul className="mt-5 grid gap-2.5">
          {connection.request.scopes.map((scope) => (
            <li className="flex items-start gap-2.5 text-sm" key={scope}>
              <Check className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0">{t(scopeDescriptionKeys[scope] ?? "connect.scopeUnknown")}</span>
            </li>
          ))}
        </ul>

        <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground">
          <ShieldCheck className="mt-px size-4 shrink-0" />
          <span className="min-w-0">{t("connect.boundary")}</span>
        </div>

        {error ? <p className="mt-4 text-xs text-destructive">{error}</p> : null}

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button disabled={connection.isAnswering} onClick={() => void answer(false)} variant="ghost">
            <X data-icon="inline-start" />
            {t("connect.decline")}
          </Button>
          <Button disabled={connection.isAnswering} onClick={() => void answer(true)}>
            {connection.isApproving ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <Check data-icon="inline-start" />}
            {t("connect.allow")}
          </Button>
        </div>
      </div>
    </main>
  );
}

// Scopes named as capabilities, not as strings from a protocol.
const scopeDescriptionKeys: Record<string, string> = {
  read: "connect.scopeRead",
  "write:sprints": "connect.scopeSprints",
  "write:work_items": "connect.scopeWorkItems",
  "write:workspace": "connect.scopeWorkspaceAdmin",
};
